import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
  type ChartOptions,
} from 'chart.js';
import { mergeWith } from 'lodash-es';
import React from 'react';
import { Line } from 'react-chartjs-2';
import { ChartContainer, ChartContainerProps } from './ChartContainer';
import { defaultPluginsOption } from './constants';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
);

const baseOptions: ChartOptions<'line'> = {
  responsive: true,
  interaction: { intersect: false, mode: 'index' },
  plugins: {
    ...defaultPluginsOption,
    legend: {
      position: 'bottom',
      labels: {
        generateLabels: chart => {
          const labels =
            ChartJS.defaults.plugins.legend.labels.generateLabels(chart);
          return labels.map(label => ({
            ...label,
            fillStyle: label.strokeStyle ?? label.fillStyle,
          }));
        },
      },
    },
  },
  scales: {
    y: {
      beginAtZero: true,
    },
  },
};

const baseDatasetOptions = {
  borderWidth: 5,
  tension: 0.1,
  pointRadius: 0,
  pointBorderWidth: 0,
  pointHoverBorderWidth: 0,
  pointBorderColor: 'transparent',
  pointHoverBorderColor: 'transparent',
  pointHoverRadius: 6,
};

type LineChartProps = Omit<React.ComponentProps<typeof Line>, 'title'> & {
  fill?: boolean;
  containerRef?: React.ForwardedRef<HTMLDivElement>;
} & Pick<ChartContainerProps, 'title'>;

export const LineChart = ({
  fill,
  options,
  data,
  title,
  height = '100%',
  containerRef,
  ...props
}: LineChartProps) => {
  const modifiedDatasets = data.datasets.map(dataset => ({
    ...baseDatasetOptions,
    ...dataset,
    fill,
  }));
  return (
    <ChartContainer title={title} ref={containerRef}>
      <Line
        {...props}
        height={height}
        options={mergeWith(baseOptions, options)}
        data={{ ...data, datasets: modifiedDatasets }}
      />
    </ChartContainer>
  );
};
